/*
 * "Hangar creates the EAS project" (SPEC §9.1.1): pure rules of the Connect app step and the app page's EAS card, no React.
 * Which apps get an EAS project, whether the card offers to create it, what the app's app.json must carry, and what to do
 * about each way `POST /apps/{id}/eas/provision` can fail (the API words the problem; this only picks the way out).
 */
import { AppFramework } from '@hangar/shared';
import type { App, EasSetup } from '@hangar/shared';

const EAS_FRAMEWORKS: readonly AppFramework[] = ['expo', 'react_native'];

/** Only Expo and React Native apps build on EAS, so only they have an EAS project. */
export const hasEasProject = (framework: AppFramework | ''): boolean => framework !== '' && EAS_FRAMEWORKS.includes(framework);

/** The Connect app step asks Hangar for the project unless the person linked one by hand (the advanced override). */
export const shouldProvisionOnConnect = (framework: AppFramework | '', easProjectId: string): boolean => hasEasProject(framework) && easProjectId.trim() === '';

/** The app page offers "Create EAS project" for an Expo / React Native app that has none (and is not archived). */
export const offersEasCreate = (app: Pick<App, 'framework' | 'easProjectId' | 'archivedAt'>): boolean => hasEasProject(app.framework) && !app.easProjectId && !app.archivedAt;

/**
 * What the app's `app.json` must carry, as the text to paste: `expo.owner`, `expo.slug` and `expo.extra.eas.projectId`.
 * For a project linked by hand only the id is known, so only that is shown (owner and slug come from expo.dev).
 */
export function appJsonSnippet(setup: Pick<EasSetup, 'projectId' | 'appConfig'>): string | null {
  if (setup.appConfig) return JSON.stringify({ expo: { owner: setup.appConfig.owner, slug: setup.appConfig.slug, extra: { eas: { projectId: setup.appConfig.extra.eas.projectId } } } }, null, 2);
  return setup.projectId ? JSON.stringify({ expo: { extra: { eas: { projectId: setup.projectId } } } }, null, 2) : null;
}

export const EAS_SOURCE_LABEL: Record<NonNullable<EasSetup['source']>, string> = {
  hangar_created: 'Created by Hangar',
  hangar_reused: 'Existing project linked',
  manual: 'Linked by hand',
};

/** Where a failed provision sends the person: Integrations (the EAS account or its token), the slug field, or just try again. */
export type EasFix = 'integrations' | 'slug' | 'retry';

const INTEGRATIONS_REASONS = new Set(['no_eas_account', 'no_credential', 'credential_revoked', 'undecryptable', 'eas_account_not_found', 'eas_forbidden', 'credential_rejected']);

/** The way out of a failed `POST /apps/{id}/eas/provision`, from its `context.reason`. */
export function easFixOf(error: unknown): EasFix {
  const { reason, pointers } = problemFacts(error);
  if (reason !== undefined && INTEGRATIONS_REASONS.has(reason)) return 'integrations';
  if (reason === 'slug_unavailable' || reason === 'eas_project_in_use' || pointers.includes('/slug')) return 'slug';
  return 'retry';
}

/** The `context.reason` and the field pointers of an ApiError, read structurally (this module never imports the API client, which needs `window`). */
function problemFacts(error: unknown): { reason: string | undefined; pointers: string[] } {
  const e = error as { details?: unknown; errors?: unknown } | null;
  const details = typeof e?.details === 'object' && e.details !== null ? (e.details as { reason?: unknown }) : {};
  const errors = Array.isArray(e?.errors) ? (e.errors as { pointer?: unknown }[]) : [];
  return { reason: typeof details.reason === 'string' ? details.reason : undefined, pointers: errors.flatMap((x) => (typeof x.pointer === 'string' ? [x.pointer] : [])) };
}

/** The toast after Connect app could not create the project: the app exists, the fix is on its page (clients: no provider named). */
export const connectedWithoutEasTitle = (appName: string, seesProviders = false): string =>
  seesProviders ? `${appName} connected, but its EAS project was not created` : `${appName} connected, but its build setup is not ready yet`;

/**
 * The frameworks Connect app offers: every one Hangar can build. Flutter is left out of the MVP (no build path: neither
 * the build service nor the generated native workflows build it; docs/after-mvp.md, v2). Apps already stored keep theirs.
 */
export const CONNECTABLE_FRAMEWORKS: readonly AppFramework[] = AppFramework.options.filter((f) => f !== 'flutter');
